import React, { useEffect, useMemo, useState } from 'react';
import {
	Box,
	Button,
	Checkbox,
	Container,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	FormLabel,
	InputLabel,
	MenuItem,
	Radio,
	RadioGroup,
	Select,
	Tab,
	Tabs,
	Typography,
} from '@mui/material';
import { EntityType, type PropertyDefinition, type PropertyDefinitionBelongsTo, type PropertyDefinitionHas, type Repository } from '../types';
import { useGetRepositoryByIdQuery } from '../services/repositoryEntries';
import { useAppSelector } from '../hooks';
import { selectIsAuthenticated } from '../containers/authSlice';
import { selectAllPropertyDefinitions } from '../containers/contentSlice';

export interface AddAnchorDialogProps {
	open: boolean;
	repositories: Repository[];
	onClose: () => void;
	elementSelectSlot?: React.ReactNode;
	onSubmit: (payload: {
		repositoryId: string;
		axisRepositoryId: string;
		direction: string;
		autoAssignPropertyId: string;
		autoAssignPropertySlug: string;
		autoPopulateReferences: boolean;
	}) => void;
}

function axisPropertiesForCollection(
	collectionRepositoryId: string,
	definitions: PropertyDefinition[],
): PropertyDefinition[] {
	if (!collectionRepositoryId) {
		return [];
	}
	return definitions.filter(
		(d) =>
			d.parentId === collectionRepositoryId
			&& (d.type === EntityType.PropertyDefinitionBelongsTo || d.type === EntityType.PropertyDefinitionHas),
	);
}

type AnchorDimensions = 'one' | 'two';

const AddAnchorDialog: React.FC<AddAnchorDialogProps> = ({
	open,
	repositories,
	onClose,
	onSubmit,
	elementSelectSlot,
}) => {
	const [repositoryId, setRepositoryId] = useState('');
	const [dimensions, setDimensions] = useState<AnchorDimensions>('one');
	const [primaryAxisPropertyId, setPrimaryAxisPropertyId] = useState('');
	const [primaryLayoutVertical, setPrimaryLayoutVertical] = useState(true);
	const [autoPopulateReferences, setAutoPopulateReferences] = useState(true);
	const isAuthenticated = useAppSelector(selectIsAuthenticated);
	const allPropertyDefinitions = useAppSelector(selectAllPropertyDefinitions);

	const { data: repositoryById } = useGetRepositoryByIdQuery(repositoryId, {
		skip: !open || !repositoryId || !isAuthenticated,
	});

	const localPropertyDefinitions = useMemo(
		() => allPropertyDefinitions.filter((def) => def.parentId === repositoryId),
		[allPropertyDefinitions, repositoryId],
	);

	const definitionsForAxis = useMemo(
		() => (isAuthenticated ? (repositoryById?.propertyDefinitions ?? []) : localPropertyDefinitions),
		[isAuthenticated, repositoryById?.propertyDefinitions, localPropertyDefinitions],
	);

	const axisOptions = useMemo(
		() => axisPropertiesForCollection(repositoryId, definitionsForAxis),
		[repositoryId, definitionsForAxis],
	);

	useEffect(() => {
		if (!open) {
			return;
		}
		setRepositoryId('');
		setDimensions('one');
		setPrimaryAxisPropertyId('');
		setPrimaryLayoutVertical(true);
		setAutoPopulateReferences(true);
	}, [open, repositories]);

	useEffect(() => {
		if (!primaryAxisPropertyId) return;
		const validIds = new Set(axisOptions.map((d) => d.id));
		if (!validIds.has(primaryAxisPropertyId)) {
			setPrimaryAxisPropertyId('');
		}
	}, [axisOptions, primaryAxisPropertyId]);

	const isTwoDimensional = axisOptions.length > 0 && dimensions === 'two';

	const handleDimensionsChange = (_event: React.SyntheticEvent, value: AnchorDimensions) => {
		setDimensions(value);
		if (value === 'one') {
			setPrimaryAxisPropertyId('');
		}
	};

	const handleSubmit = (event: React.FormEvent) => {
		event.preventDefault();
		const selectedProp = axisOptions.find((d) => d.id === primaryAxisPropertyId) as
			| PropertyDefinitionBelongsTo
			| PropertyDefinitionHas
			| undefined;
		const axisRepositoryId = selectedProp?.targetRepositoryId ?? repositoryId;
		if (!repositoryId) return;
		// Columns (primaryLayoutVertical=true): group headers spread along X → main anchor direction 'horizontal'
		// Rows (primaryLayoutVertical=false): group headers stack along Y → main anchor direction 'vertical'
		const direction = primaryLayoutVertical ? 'horizontal' : 'vertical';
		onSubmit({ repositoryId, axisRepositoryId, direction, autoAssignPropertyId: primaryAxisPropertyId, autoAssignPropertySlug: selectedProp?.slug ?? '', autoPopulateReferences });
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth scroll="body">
			<form onSubmit={handleSubmit} autoComplete="off">
				{elementSelectSlot != null ? (
					<Box display="flex" justifyContent="space-between" alignItems="center">
						<DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>Add anchor</DialogTitle>
						<Box pr={2}>{elementSelectSlot}</Box>
					</Box>
				) : (
					<DialogTitle>Add data grid</DialogTitle>
				)}
				<Container>
					<Typography variant="body2" sx={{color: 'text.secondary'}}>
						{'Use anchors to visually group data entries within your board. You can configure side-effects to anchors, such as making entries gain a certain property when placed onto the anchor.'}
					</Typography>
				</Container>
				<DialogContent>
					<Box display="flex" flexDirection="column" gap={2} mt={elementSelectSlot != null ? 0 : 1}>
						<FormControl fullWidth size="small" required>
							<InputLabel id="add-grid-repository-label">Collection</InputLabel>
							<Select
								labelId="add-grid-repository-label"
								label="Collection"
								value={repositoryId}
								onChange={(e) => setRepositoryId(String(e.target.value))}
							>
								<MenuItem value="">
									<em>None</em>
								</MenuItem>
								{repositories.map((r) => (
									<MenuItem key={`grid-repo-${r.id}`} value={r.id}>
										{r.title}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						{axisOptions.length > 0 && (
							<Box>
								<Tabs
									value={dimensions}
									onChange={handleDimensionsChange}
									variant="fullWidth"
									sx={{ borderBottom: 1, borderColor: 'divider' }}
								>
									<Tab value="one" label="One-dimensional" />
									<Tab value="two" label="Two-dimensional" />
								</Tabs>
								<Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
									{isTwoDimensional
										? 'Entries are grouped into columns or rows by a related property.'
										: 'Entries are laid out in a single line, horizontally or vertically.'}
								</Typography>
							</Box>
						)}
						<Box display="flex" flexDirection="column" gap={1.5}>
							{isTwoDimensional && (
								<FormControl fullWidth size="small" required>
									<InputLabel id="add-grid-primary-axis-label">Group by</InputLabel>
									<Select
										labelId="add-grid-primary-axis-label"
										label="Group by"
										value={primaryAxisPropertyId}
										onChange={(e) => setPrimaryAxisPropertyId(String(e.target.value))}
									>
										{axisOptions.map((d) => (
											<MenuItem key={`primary-prop-${d.id}`} value={d.id}>
												{d.label}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							)}

							<FormControl component="fieldset" variant="standard" sx={{ m: 0 }}>
								<FormLabel component="legend" sx={{ typography: 'body2', color: 'text.primary', mb: 0.5 }}>
									{isTwoDimensional ? 'Layout' : 'Direction'}
								</FormLabel>
								<RadioGroup
									row
									value={primaryLayoutVertical ? 'vertical' : 'horizontal'}
									onChange={(e) => setPrimaryLayoutVertical(e.target.value === 'vertical')}
								>
									<FormControlLabel
										value="vertical"
										control={<Radio size="small" />}
										label={isTwoDimensional ? 'Columns' : 'Horizontal'}
									/>
									<FormControlLabel
										value="horizontal"
										control={<Radio size="small" />}
										label={isTwoDimensional ? 'Rows' : 'Vertical'}
									/>
								</RadioGroup>
							</FormControl>
						</Box>
					</Box>
					{repositoryId && (
						<FormControlLabel
							control={
								<Checkbox
									size="small"
									checked={autoPopulateReferences}
									onChange={(e) => setAutoPopulateReferences(e.target.checked)}
								/>
							}
							label="Populate entries"
							sx={{
								userSelect: 'none'
							}}
						/>
					)}
				</DialogContent>
				<DialogActions>
					<Button type="button" onClick={onClose}>
						Cancel
					</Button>
					<Button type="submit" variant="contained">
						Add
					</Button>
				</DialogActions>
			</form>
		</Dialog>
	);
};

export default AddAnchorDialog;
